import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BillingBanner } from "../components/BillingBanner";
import {
  api,
  REST_AUTH_EXAMPLE,
  type CompanySummary,
  type DashboardResponse,
  type WatchEventItem,
} from "../lib/api";
import { companyDetailPath } from "../lib/companyUrl";
import { isDistressKind } from "../lib/eventSeverity";
import { formatMoney } from "../lib/money";
import { isLocked } from "../lib/plans";
import { relativeTime } from "../lib/time";
import { useConnectedCount } from "../lib/useConnectedCount";
// Same generated taxonomy the Watchlists page reads labels from
// (schemas/events.yaml → schemas/generated/ts/watch_events) — the
// activity feed can't drift from what the worker actually emits.
import { EVENT_LABELS as GEN_EVENT_LABELS } from "../../../../schemas/generated/ts/watch_events";
import watchlistEyeSvg from "../theme/icons/watchlist-eye.svg?raw";
import aiSparkleSvg from "../theme/icons/ai-sparkle.svg?raw";
import apikeysKeySvg from "../theme/icons/apikeys-key.svg?raw";

const EVENT_LABELS: Record<string, string> = GEN_EVENT_LABELS;

// Cap on the watched-companies list before "See all N →" expands it —
// keeps the page from firing dozens of lazy per-card financial fetches
// on first paint for a heavily-watched account.
const WATCHED_CAP = 12;

// Lazily fetches this one card's headline financials on mount. Errors
// degrade the CARD, not the page: a 502 (upstream unavailable) says so
// plainly; any other error (403 not-watched race, 429 rate-limited under
// a burst of card mounts, network blip) fails quiet — the card just
// shows no financials rather than an alarming error message, since none
// of those are actionable from here.
function CompanyCardFinancials({ companyId }: { companyId: string }) {
  type State =
    | { kind: "loading" }
    | { kind: "ok"; data: CompanySummary }
    | { kind: "unavailable" }
    | { kind: "hidden" };

  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    setState({ kind: "loading" });
    api
      .companySummary(companyId)
      .then((data) => {
        if (alive) setState({ kind: "ok", data });
      })
      .catch((e) => {
        if (!alive) return;
        const msg = (e as Error).message;
        setState({
          kind:
            msg === "summary_upstream_unavailable" ? "unavailable" : "hidden",
        });
      });
    return () => {
      alive = false;
    };
  }, [companyId]);

  if (state.kind === "loading") {
    // Mirrors the three-figure row below so the card doesn't jump when the
    // real financials land. The blocks are decorative (aria-hidden); an
    // sr-only line carries the loading cue that the skeleton replaced.
    return (
      <div style={{ marginTop: "var(--space-3)" }}>
        <span className="sr-only">Loading financials…</span>
        <div style={{ display: "flex", gap: "var(--space-5)" }} aria-hidden>
          <span className="skeleton fig" />
          <span className="skeleton fig" />
          <span className="skeleton fig" />
        </div>
      </div>
    );
  }
  if (state.kind === "unavailable") {
    return (
      <p
        className="muted"
        style={{ margin: "var(--space-3) 0 0", fontSize: 12 }}
      >
        Financials unavailable.
      </p>
    );
  }
  if (state.kind === "hidden") return null;

  const s = state.data;
  const runway = s.runway_months.value;
  return (
    <div className="card-financials">
      <div className="card-metrics">
        <div className="card-metric">
          <div className="eyebrow">Turnover</div>
          <div className="card-metric-value mono">
            {formatMoney(s.turnover.value, s.currency)}
          </div>
        </div>
        <div className="card-metric">
          <div className="eyebrow">Profit</div>
          <div className="card-metric-value mono">
            {formatMoney(s.profit_loss.value, s.currency)}
          </div>
        </div>
        <div className="card-metric">
          <div className="eyebrow">Cash</div>
          <div className="card-metric-value mono">
            {formatMoney(s.cash.value, s.currency)}
          </div>
        </div>
      </div>
      {(runway != null || s.hygiene_score != null) && (
        <div className="chip-row">
          {runway != null && (
            // Runway is a derived figure and comes through with a long
            // fractional tail; whole months is the useful precision.
            <span className="chip">Runway {Math.round(runway)} mo</span>
          )}
          {s.hygiene_score != null && (
            <span className="chip">Hygiene {s.hygiene_score}</span>
          )}
        </div>
      )}
      {s.current_period_end && (
        <p className="card-asof">as filed, period ending {s.current_period_end}</p>
      )}
    </div>
  );
}

// One row of the recent-activity feed: severity dot, event label, company
// name linked to the internal company detail page (a free view — activity
// only ever covers watched companies), relative time with the absolute
// value as the hover title.
function ActivityRow({ ev, first }: { ev: WatchEventItem; first: boolean }) {
  const name = ev.company_name ?? ev.company_id;
  // Link to the internal detail page only for a namespaced id it can
  // resolve; a non-namespaced id (no detail page) renders as plain text
  // rather than a link to a guaranteed 404.
  const to = companyDetailPath(ev.company_id);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) 0",
        borderTop: first ? "none" : "1px solid var(--color-border-subtle)",
      }}
    >
      <span
        className={`status-dot${isDistressKind(ev.event_kind) ? " risk" : ""}`}
        aria-hidden
      />
      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
        <strong>{EVENT_LABELS[ev.event_kind] ?? ev.event_kind}</strong>{" "}
        <span className="muted">
          · {to ? <Link to={to}>{name}</Link> : name}
        </span>
      </span>
      <span
        className="muted"
        style={{ fontSize: 12, whiteSpace: "nowrap" }}
        title={new Date(ev.occurred_at).toLocaleString()}
      >
        {relativeTime(ev.occurred_at)}
      </span>
    </div>
  );
}

// First-paint placeholder sketching the loaded page (stat row + a list
// card) while GET /dashboard resolves. The status live region announces
// its text content — an sr-only line, since a live region reads its text,
// not an aria-label — while the blocks themselves stay decorative.
function DashboardSkeleton() {
  return (
    <div role="status">
      <span className="sr-only">Loading overview…</span>
      <div className="stat-row" aria-hidden>
        {[0, 1, 2].map((i) => (
          <div className="card stat-tile" key={i}>
            <span className="skeleton" style={{ width: "40%" }} />
            <span
              className="skeleton lg"
              style={{ width: "30%", margin: "var(--space-2) 0" }}
            />
            <span className="skeleton" style={{ width: "55%" }} />
          </div>
        ))}
      </div>
      <div className="card" aria-hidden>
        {["70%", "55%", "62%"].map((w, i) => (
          <span
            className="skeleton"
            key={i}
            style={{ width: w, marginTop: i === 0 ? 0 : "var(--space-4)" }}
          />
        ))}
      </div>
    </div>
  );
}

export function Dashboard() {
  const connected = useConnectedCount();
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api
      .dashboard()
      .then(setData)
      .catch((e) => setErr(String((e as Error).message)));
  }, []);

  // No entitlement row reads the same as locked — neither state has a
  // subscription to gate financials on.
  const locked = isLocked(data?.entitlement);
  const watchlists = data?.watchlists ?? [];
  const shown = showAll ? watchlists : watchlists.slice(0, WATCHED_CAP);

  return (
    <div className="main">
      <div className="page-header">
        <h1>Overview</h1>
      </div>
      <p className="page-sub">
        Filed financials and register events for the companies you watch —
        updated as they file.
      </p>

      {err && <p className="error">{err}</p>}
      {!data && !err && <DashboardSkeleton />}

      {data && (
        <>
          <BillingBanner entitlement={data.entitlement} />

          {watchlists.length === 0 ? (
            <>
              <div className="card agents-empty">
                <span
                  className="hero-mark"
                  aria-hidden
                  dangerouslySetInnerHTML={{ __html: watchlistEyeSvg }}
                />
                <h2>Nothing watched yet</h2>
                <p className="muted">
                  Track a company to see its financials and events here —
                  accounts filed, charges, status changes, insolvency.
                </p>
                <Link
                  to="/watchlists"
                  className="btn primary"
                  style={{ marginTop: "var(--space-4)", display: "inline-flex" }}
                >
                  Watch a company
                </Link>
              </div>
              <h2>Get started</h2>
              <div className="path-grid">
                <Link to="/watchlists" className="card path-card">
                  <span
                    className="path-icon"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: watchlistEyeSvg }}
                  />
                  <div className="path-title">Watch companies</div>
                  <p className="path-desc">
                    Filings, charges, status changes and insolvency land on
                    this page — with alerts by email or webhook.
                  </p>
                  <span className="path-cta">Open Watchlists →</span>
                </Link>
                <Link to="/connected-apps" className="card path-card">
                  <span
                    className="path-icon"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: aiSparkleSvg }}
                  />
                  <div className="path-title">Connect an agent</div>
                  <p className="path-desc">
                    Give Claude or ChatGPT live as-reported financials over
                    MCP — sign in and approve, no code needed.
                  </p>
                  <span className="path-cta">Open Agents →</span>
                </Link>
                <Link to="/keys" className="card path-card">
                  <span
                    className="path-icon"
                    aria-hidden
                    dangerouslySetInnerHTML={{ __html: apikeysKeySvg }}
                  />
                  <div className="path-title">Build with the API</div>
                  <p className="path-desc">
                    Create a key and call the REST API directly with{" "}
                    <code>{REST_AUTH_EXAMPLE}</code>.
                  </p>
                  <span className="path-cta">Create an API key →</span>
                </Link>
              </div>
              <p className="muted path-foot">
                Whichever route you take, consumption shows up under{" "}
                <Link to="/usage">Usage</Link>.
              </p>
            </>
          ) : (
            <>
              <div className="stat-row">
                <Link to="/watchlists" className="card stat-tile">
                  <div className="eyebrow">Tracking</div>
                  <div className="stat-value">
                    {watchlists.length}
                    {data.watch_limit ? ` of ${data.watch_limit}` : ""}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    companies
                  </div>
                </Link>
                <div className="card stat-tile">
                  <div className="eyebrow">This week</div>
                  <div className="stat-value">{data.events_7d}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    events
                  </div>
                </div>
                <Link to="/connected-apps" className="card stat-tile">
                  <div className="eyebrow">Agents</div>
                  <div className="stat-value">{connected ?? 0}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {/* The "connect" nudge only shows once we KNOW the count
                        is zero — while connected is null (still loading) we
                        stay on the neutral label, so an account that does
                        have agents never flashes the call-to-action. */}
                    {connected === 0
                      ? "connect Claude or ChatGPT →"
                      : "connected"}
                  </div>
                </Link>
              </div>

              {!locked && (
                <>
                  <div className="section-head">
                    <h2>Watched companies</h2>
                    <Link to="/watchlists" className="section-link">
                      Manage watchlists →
                    </Link>
                  </div>
                  <div className="entity-list">
                    {shown.map((w) => {
                      const initial =
                        (w.company_name ?? w.company_id)
                          .trim()
                          .charAt(0)
                          .toUpperCase() || "•";
                      const name = w.company_name ?? w.company_id;
                      // Watched ids are always namespaced (the watchlist only
                      // accepts watchable registers), so this resolves; gated
                      // for consistency with the activity feed.
                      const to = companyDetailPath(w.company_id);
                      return (
                        <div className="entity-card" key={w.id}>
                          <span className="entity-avatar" aria-hidden>
                            {initial}
                          </span>
                          <div className="entity-body">
                            <div className="entity-name">
                              {/* Internal company detail page (free view —
                                  watched companies never draw a credit),
                                  not the public landing page: the portal
                                  holds the richer, entitled view. */}
                              {to ? <Link to={to}>{name}</Link> : name}
                            </div>
                            <div className="entity-meta">
                              <span className="mono">{w.company_id}</span>
                            </div>
                            <CompanyCardFinancials companyId={w.company_id} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!showAll && watchlists.length > WATCHED_CAP && (
                    <p style={{ marginTop: "var(--space-3)" }}>
                      <button
                        type="button"
                        className="btn secondary"
                        onClick={() => setShowAll(true)}
                      >
                        See all {watchlists.length} →
                      </button>
                    </p>
                  )}
                </>
              )}

              <div className="section-head">
                <h2>Recent activity</h2>
                {/* Locked accounts don't render the watched-companies
                    section (which carries this link) — keep exactly one
                    route to Watchlists visible either way. */}
                {locked && (
                  <Link to="/watchlists" className="section-link">
                    Manage watchlists →
                  </Link>
                )}
              </div>
              <div className="card">
                {data.recent_events.length === 0 ? (
                  <p className="muted" style={{ margin: 0 }}>
                    No events yet — events appear here as your watched
                    companies file.
                  </p>
                ) : (
                  data.recent_events.map((ev, i) => (
                    <ActivityRow key={ev.id} ev={ev} first={i === 0} />
                  ))
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
